/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */

import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

let nextId = 0;

export interface Tag {
  title: string;
  value: string;
}

@Component({
  selector: 'ut-tag-selector',
  templateUrl: './tag-selector.component.html',
  styleUrls: ['./tag-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TagSelectorComponent implements OnInit, OnChanges {
  @Input() availableTags: Tag[] = [];
  @Input() selectedTags: Tag[] = [];
  @Input() maxTags = 2;
  @Input() placeholder = 'Add up to 2 tags';
  // DOM id of the search input, so a page's <label for> can name it.
  @Input() inputId: string | null = null;
  @Output() selectedTagsChange = new EventEmitter<Tag[]>();

  @ViewChild('searchInput', { static: false })
    searchInput?: ElementRef<HTMLInputElement>;

  dropdownShown = false;
  searchText = '';
  filteredTags: Tag[] = [];
  focusedIndex = -1;
  showDropdownAbove = false;

  readonly listboxId = `tagSelectorListbox${nextId++}`;
  readonly selectedId = `${this.listboxId}-selected`;

  private readonly KEY_CODES = {
    TAB: 9,
    RETURN: 13,
    ESC: 27,
    SPACE: 32,
    UP: 38,
    DOWN: 40,
    BACKSPACE: 8,
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private liveAnnouncer: LiveAnnouncer,
  ) {}

  ngOnInit(): void {
    this.updateFilteredTags();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When availableTags changes (e.g., loaded from network), update filtered tags
    if (changes.availableTags && !changes.availableTags.firstChange) {
      this.updateFilteredTags();
      this.cdr.markForCheck();
    }

    // When selectedTags changes from parent, update filtered tags to reflect selection state
    if (changes.selectedTags && !changes.selectedTags.firstChange) {
      this.updateFilteredTags();
      this.cdr.markForCheck();
    }
  }

  toggleDropdown(): void {
    this.dropdownShown = !this.dropdownShown;

    if (this.dropdownShown) {
      this.calculateDropdownPosition();
      this.updateFilteredTags();
      this.cdr.markForCheck();
      setTimeout(() => {
        this.searchInput?.nativeElement.focus();
      }, 10);
    } else {
      this.searchText = '';
      this.focusedIndex = -1;
      this.cdr.markForCheck();
    }
  }

  openDropdown(): void {
    if (!this.dropdownShown) {
      this.toggleDropdown();
    }
  }

  closeDropdown(): void {
    if (this.dropdownShown) {
      this.toggleDropdown();
    }
  }

  onSearchChange(): void {
    // Automatically open dropdown when user starts typing
    if (!this.dropdownShown && this.searchText.length > 0) {
      this.openDropdown();
    }

    this.updateFilteredTags();

    // Visually highlight the first item in the filtered list (but keep focus in input)
    if (this.dropdownShown && this.filteredTags.length > 0) {
      this.focusedIndex = 0;
    } else {
      this.focusedIndex = -1;
    }

    this.cdr.markForCheck();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    switch (event.keyCode) {
      case this.KEY_CODES.DOWN:
        event.preventDefault();
        if (this.filteredTags.length > 0) {
          // Move to next item, or to first item if nothing selected
          this.openDropdown();
          if (this.focusedIndex === -1) {
            this.focusedIndex = 0;
          } else if (this.focusedIndex < this.filteredTags.length - 1) {
            this.focusedIndex++;
          }
          this.scrollActiveIntoView();
        }
        break;
      case this.KEY_CODES.UP:
        event.preventDefault();
        if (this.filteredTags.length > 0 && this.focusedIndex > 0) {
          // Move to previous item
          this.focusedIndex--;
          this.scrollActiveIntoView();
        }
        break;
      case this.KEY_CODES.RETURN:
        event.preventDefault();
        // Select the active item when pressing Enter
        if (this.dropdownShown && this.focusedIndex >= 0 && this.filteredTags[this.focusedIndex]) {
          this.toggleTagSelection(this.filteredTags[this.focusedIndex]);
        }
        break;
      case this.KEY_CODES.ESC:
        this.closeDropdown();
        break;
      case this.KEY_CODES.BACKSPACE:
        if (this.searchText === '' && this.selectedTags.length > 0) {
          this.removeTag(this.selectedTags[this.selectedTags.length - 1]);
        }
        break;
      default:
        break;
    }
  }

  onOptionClick(tag: Tag): void {
    this.toggleTagSelection(tag);
  }

  optionId(index: number): string {
    return `${this.listboxId}-option-${index}`;
  }

  get activeOptionId(): string | null {
    return this.dropdownShown && this.focusedIndex >= 0 && this.filteredTags[this.focusedIndex]
      ? this.optionId(this.focusedIndex)
      : null;
  }

  removeTag(tag: Tag): void {
    this.selectedTags = this.selectedTags.filter((t) => t.value !== tag.value);
    this.selectedTagsChange.emit(this.selectedTags);
    this.liveAnnouncer.announce(`${tag.title} removed, ${this.selectedTags.length} of ${this.maxTags} tags`, 'polite');
    this.updateFilteredTags();
    this.cdr.markForCheck();
  }

  isTagSelected(tag: Tag): boolean {
    return this.selectedTags.some((t) => t.value === tag.value);
  }

  get maxTagsReached(): boolean {
    return this.selectedTags.length >= this.maxTags;
  }

  get ariaLabel(): string {
    if (this.selectedTags.length === 0) {
      return this.placeholder;
    }
    return `Selected tags: ${this.selectedTags.map((t) => t.title).join(', ')}`;
  }

  getHighlightedTitle(title: string): string {
    if (!this.searchText) {
      return title;
    }

    const searchLower = this.searchText.toLowerCase();
    const titleLower = title.toLowerCase();
    const index = titleLower.indexOf(searchLower);

    if (index === -1) {
      return title;
    }

    const before = title.substring(0, index);
    const match = title.substring(index, index + this.searchText.length);
    const after = title.substring(index + this.searchText.length);

    return `${before}<strong>${match}</strong>${after}`;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    // Check if the new focus target is outside the component
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (!relatedTarget || !this.elementRef.nativeElement.contains(relatedTarget)) {
      this.closeDropdown();
    }
  }

  @HostListener('keydown.escape')
  onEscapeKey(): void {
    if (this.dropdownShown) {
      this.closeDropdown();
    }
  }

  private toggleTagSelection(tag: Tag): void {
    // Selected tags are now hidden from dropdown, so this will only add tags
    if (!this.maxTagsReached) {
      this.selectedTags = [...this.selectedTags, tag];
      this.selectedTagsChange.emit(this.selectedTags);

      this.liveAnnouncer.announce(`${tag.title} added, ${this.selectedTags.length} of ${this.maxTags} tags`, 'polite');

      // Clear search text and close the list after selection. Focus never left the input, so the (focus) handler
      // that opens the list does not fire again and reopen it.
      this.searchText = '';
      this.updateFilteredTags();
      this.closeDropdown();
      this.cdr.markForCheck();
    }
  }

  private updateFilteredTags(): void {
    const searchLower = this.searchText.toLowerCase();
    this.filteredTags = this.availableTags.filter((tag) => {
      const matchesSearch = tag.title.toLowerCase().includes(searchLower);
      const notSelected = !this.isTagSelected(tag);
      return matchesSearch && notSelected;
    });
  }

  // The active option is marked with aria-activedescendant on the input; this only keeps it visible in the list.
  private scrollActiveIntoView(): void {
    this.cdr.markForCheck();
    setTimeout(() => document.getElementById(this.optionId(this.focusedIndex))?.scrollIntoView({ block: 'nearest' }));
  }

  private calculateDropdownPosition(): void {
    const containerElement = this.elementRef.nativeElement;
    if (!containerElement) {
      return;
    }

    // Get container position relative to viewport
    const rect = containerElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Dropdown max-height is 30rem = 300px (assuming 1rem = 10px)
    const dropdownMaxHeight = 300;
    const spacingBuffer = 10; // Extra buffer for margin and spacing

    // Calculate available space below and above
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Show above if not enough space below but more space available above
    this.showDropdownAbove = spaceBelow < (dropdownMaxHeight + spacingBuffer)
                             && spaceAbove > spaceBelow;
  }
}
